import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {fetchBillingStatus} from './actions';
import {IBillingStatus} from '../api';

export type BillingStatus = 'active' | 'grace' | 'restricted' | 'unknown';

export interface BillingState {
    status: BillingStatus;
    threshold_warning: 1 | 2 | 3 | null;
    forecast_days: number | null;
    is_enabled: boolean;
    balance: number;
    credit_limit: number;
    daily_cost: number;
    unit: string;
    blocked_at: string | null;
    is_loading: boolean;
    last_fetched_at: number | null;
    connected: boolean;
    /** Сигнатура текущего нотифицируемого события (для дедупликации модалки через localStorage) */
    current_signature: string | null;
    /** Открыта ли модалка с уведомлением */
    modal_open: boolean;
}

const initialState: BillingState = {
    status: 'unknown',
    threshold_warning: null,
    forecast_days: null,
    is_enabled: true,
    balance: 0,
    credit_limit: 0,
    daily_cost: 0,
    unit: 'CR',
    blocked_at: null,
    is_loading: false,
    last_fetched_at: null,
    connected: false,
    current_signature: null,
    modal_open: false,
};

const billingSlice = createSlice({
    name: 'billing',
    initialState,
    reducers: {
        setBillingConnected(state, action: PayloadAction<boolean>) {
            state.connected = action.payload;
        },
        /**
         * Принудительная установка restricted. Дёргается из axios-interceptor'а
         * при получении HTTP 402 — чтобы UI обновился мгновенно, не дожидаясь fetch.
         */
        forceRestricted(state) {
            state.status = 'restricted';
            state.is_enabled = false;
        },
        openBillingModal(state) {
            state.modal_open = true;
        },
        closeBillingModal(state) {
            state.modal_open = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchBillingStatus.pending, (state) => {
                state.is_loading = true;
            })
            .addCase(fetchBillingStatus.fulfilled, (state, action) => {
                const d = action.payload;
                state.status = d.status;
                state.threshold_warning = d.threshold_warning;
                state.forecast_days = d.forecast_days;
                state.is_enabled = d.is_enabled;
                state.balance = d.balance;
                state.credit_limit = d.credit_limit;
                state.daily_cost = d.daily_cost;
                state.unit = d.unit;
                state.blocked_at = d.blocked_at;
                state.current_signature = computeSignature(d);
                state.is_loading = false;
                state.last_fetched_at = Date.now();
            })
            .addCase(fetchBillingStatus.rejected, (state) => {
                state.is_loading = false;
            });
    },
});

/**
 * Сигнатура текущего состояния. Уникальна для каждого нотифицируемого события.
 *
 * Логика:
 *   - active: null (модалку не показываем)
 *   - grace: 'grace-{N}' где N = порог (1, 2, 3)
 *   - restricted: 'restricted-{blocked_at}' — timestamp блокировки делает уникальным
 */
function computeSignature(d: IBillingStatus): string | null {
    if (d.status === 'restricted') {
        return `restricted-${d.blocked_at ?? 'unknown'}`;
    }
    if (d.status === 'grace' && d.threshold_warning !== null) {
        return `grace-${d.threshold_warning}`;
    }
    return null;
}

export const {
    setBillingConnected,
    forceRestricted,
    openBillingModal,
    closeBillingModal,
} = billingSlice.actions;

export default billingSlice.reducer;